"use client";

import * as React from 'react';
import { Button, toast, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui';
import { Download, ChevronDown } from 'lucide-react';
import { Engine, TableView } from '@/lib/engine';
import { Format, getFormatDisplayName, FORMAT_MIME_TYPES } from '../../lib/formats';
import { downloadFromUrl } from '../../lib/export-utils';
import { FileFormatIcon, getFormatColorClasses } from '../../components/file-format';

/**
 * Gets signed URLs for uploading source and destination files
 * @param sourceFormat Source file format
 * @param destinationFormat Destination file format
 * @returns Promise with upload URLs for source and destination
 */
async function getUploadUrls(sourceFormat: Format, destinationFormat: Format) {
  const response = await fetch('/api/convert/urls', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sourceFormat,
      destinationFormat
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to get upload URLs: ${error}`);
  }

  return response.json();
}

/**
 * Triggers conversion from source to destination format
 * @param sourcePath Path to the source file
 * @param sourceFormat Source file format
 * @param destinationFormat Destination file format
 * @returns Promise with the URL to download the converted file
 */
async function triggerConversion(sourcePath: string, sourceFormat: Format, destinationFormat: Format) {
  const response = await fetch('/api/convert', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sourceUrl: sourcePath,
      sourceFormat,
      destinationFormat,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to convert file: ${error}`);
  }

  return response.json();
}

interface NovaExportButtonProps {
  /**
   * The engine instance to use for exporting data
   */
  engine: Engine;
  
  /**
   * The view to export
   * This should be provided by Nova
   */
  view: TableView | null;
  
  /**
   * Optional class name for styling
   */
  className?: string;
  
  /**
   * Optional callback to run after export completes
   */
  onExportComplete?: () => void;
  
  /**
   * Optional callback to run if export fails
   */
  onExportError?: (error: Error) => void;
}

/**
 * NovaExportButton Component
 * 
 * A button component that handles exporting data from Nova to a signed S3 URL.
 */
export function NovaExportButton({
  engine,
  view,
  className,
  onExportComplete,
  onExportError
}: NovaExportButtonProps) {
  // Track loading state
  const [isExporting, setIsExporting] = React.useState(false);
  // Track selected format (default to Parquet)
  const [selectedFormat, setSelectedFormat] = React.useState<Format>(Format.PARQUET);
  
  // Handle export action
  const handleExport = async (format: Format) => {
    // If already exporting, prevent multiple clicks
    if (isExporting) return;
    
    // Update selected format
    setSelectedFormat(format);
    
    // Create a toast ID to update the same toast throughout the process
    const toastId = "export-progress";
    
    try {
      // Set loading state
      setIsExporting(true);
      
      // Check if we have a view
      if (!view) {
        throw new Error("No view available for export. Please select a table or run a query first.");
      }
      
      // Show initial toast
      toast.info(`Starting ${getFormatDisplayName(format)} export...`, { id: toastId });
      
      // Get signed URLs for upload (source will always be parquet since that's what the engine exports)
      toast.loading("Generating signed URLs...", { id: toastId });
      const sourceFormat = Format.PARQUET;
      const destinationFormat = format;
      
      // Get upload URLs - note that these are PUT-only URLs, not readable/downloadable
      const { source, destination } = await getUploadUrls(sourceFormat, destinationFormat);
      
      // Log the URLs for debugging
      console.log('Export URLs:', {
        sourceFormat,
        destinationFormat,
        sourceUploadUrl: source.uploadUrl,  // This is a PUT-only URL
        sourceFilePath: source.filePath,
        sourceContentType: source.contentType,
        destinationUploadUrl: destination?.uploadUrl,
        destinationFilePath: destination?.filePath,
        destinationContentType: destination?.contentType
      });
      
      // Update toast for export in progress
      toast.loading(`Exporting data...`, { 
        id: toastId,
        description: "This may take a moment depending on the size of your data."
      });
      
      // Call the engine's exportToURL method with the provided view
      // This uploads the data to the source.uploadUrl (which is a PUT-only URL)
      console.log(`Calling engine.exportToURL with view:`, {
        sourceType: view.source.type,
        tableRefId: view.source.tableRef.id,
        tableRefPath: view.source.tableRef.path
      });
      
      await engine.exportToURL({
        view,
        signedUrl: source.uploadUrl  // PUT-only URL
      });
      
      // We need to trigger conversion to get a readable/downloadable URL
      // The source.uploadUrl is PUT-only and can't be used for downloading
      toast.loading(`Preparing ${getFormatDisplayName(format)} for download...`, { 
        id: toastId,
        description: format === Format.PARQUET 
          ? "Finalizing your Parquet file." 
          : `Converting your data to ${getFormatDisplayName(format)}.`
      });
      
      // Trigger conversion - this gives us a readable URL we can use for downloading
      const convertResult = await triggerConversion(source.filePath, sourceFormat, destinationFormat);
      
      // Get the download URL from the conversion result - this is a GET URL we can use for downloading
      const downloadUrl = convertResult.url;
      
      // Generate a filename based on the view
      const pathParts = view.source.tableRef.path.split('/');
      const lastPart = pathParts[pathParts.length - 1] || '';
      const baseName = lastPart.split('.')[0] || 'export';
      const filename = `${baseName}-${Date.now()}.${format}`;
      
      // If we get here, the export was successful
      console.log('Export completed successfully');
      
      // Get format-specific color classes
      const colorClasses = getFormatColorClasses(format);
      
      // Create a download button component
      const DownloadButton = () => (
        <button
          className={`mt-2 px-3 py-1 rounded text-white text-sm flex items-center bg-gray-600 hover:bg-gray-700`}
          onClick={() => downloadFromUrl(downloadUrl, filename)}
        >
          <Download className="h-3.5 w-3.5 mr-1" />
          Download {getFormatDisplayName(format)}
        </button>
      );
      
      // Update toast for success with download button
      toast.success(`${getFormatDisplayName(format)} export completed`, { 
        id: toastId,
        description: (
          <div>
            <p>Your data has been successfully exported.</p>
            <DownloadButton />
          </div>
        ),
        duration: 10000 // Keep the toast visible longer so user has time to click download
      });
      
      // Call the onExportComplete callback if provided
      onExportComplete?.();
    } catch (error) {
      // Log the error
      console.error("Export error:", error);
      
      // Update toast for error
      toast.error("Export failed", { 
        id: toastId,
        description: error instanceof Error ? error.message : String(error)
      });
      
      // Call the onExportError callback if provided
      if (error instanceof Error && onExportError) {
        onExportError(error);
      }
    } finally {
      // Reset loading state
      setIsExporting(false);
    }
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={`h-8 px-2.5 flex items-center gap-1 ${className || ''}`}
          disabled={isExporting}
        >
          {isExporting ? (
            <>
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <Download className="h-3.5 w-3.5" />
              <span>Export</span>
              <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px] p-2">
        {Object.values(Format).map((format) => (
          <button
            key={format}
            onClick={() => handleExport(format)}
            disabled={isExporting}
            className="w-full flex items-center px-2 py-1.5 text-sm rounded-md hover:bg-accent mb-1 last:mb-0 text-left"
          >
            <FileFormatIcon format={format} size={16} className="mr-2 flex-shrink-0" />
            <span className="font-normal">{getFormatDisplayName(format)}</span>
          </button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 